import {
  isAuctionRelease,
  isReleaseTypeBlockchainAuction,
} from '@utils/product';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import React from 'react';

import { PurchasingState } from '../../../types/Purchasing';
import { Price as AuctionPrice } from '../../Auction/Manifold/widgets/Price';
import { ProductPrice } from '../../ProductPrice/ProductPrice';

type ProductDetailsProps = {
  artists: {
    name: string;
    slug?: string;
  }[];
  title: string;
  price?: number;
  auction?: ProductTypeNftAuction;
  releaseType?: StoryblokReleaseType;
  productState?: PurchasingState;
};

export const ProductHero: React.FC<ProductDetailsProps> = ({
  artists,
  auction,
  price,
  productState,
  releaseType,
  title,
}) => {
  return (
    <div className="flex w-full flex-col text-center">
      <h1 className="text-md font-bold md:text-lg">
        {artists.map((artist, index) => (
          <>
            {artist.slug ? (
              <Link
                key={artist.name}
                className="link font-bold"
                href={`/artists/${artist.slug}`}
              >
                {artist.name}
              </Link>
            ) : (
              <span key={artist.name}>{artist.name}</span>
            )}
            {index < artists.length - 2 && ', '}
            {index === artists.length - 2 && ' & '}
          </>
        ))}
      </h1>
      <h2 className="text-md md:text-lg">{title}</h2>

      <Price
        auction={auction}
        price={price}
        productState={productState}
        releaseType={releaseType}
      />
    </div>
  );
};

const Price = ({
  auction,
  price,
  productState,
  releaseType,
}: {
  price?: number;
  releaseType?: StoryblokReleaseType;
  auction?: ProductTypeNftAuction;
  productState?: PurchasingState;
}) => {
  if (releaseType && isReleaseTypeBlockchainAuction(releaseType)) {
    return (
      <BlockchainAuctionPrice
        auction={auction}
        productState={productState}
        releaseType={releaseType}
      />
    );
  }

  if (
    releaseType &&
    isAuctionRelease(releaseType) &&
    productState !== 'SOLD_OUT'
  )
    return null;

  if (!price) return null;
  return (
    <ProductPrice className="text-md text-neutral-4 md:text-lg" price={price} />
  );
};

const BlockchainAuctionPrice = ({
  auction,
  productState,
  releaseType,
}: Pick<ProductDetailsProps, 'auction' | 'productState' | 'releaseType'>) => {
  const { t } = useTranslation('product');

  if (
    productState === 'COMING_SOON' &&
    isReleaseTypeBlockchainAuction(releaseType || '') &&
    auction?.floorPrice
  ) {
    return (
      <div className="flex items-end justify-center gap-1 md:gap-2">
        <p className="text-md text-neutral-4 md:text-lg">
          {t('auction.from')} {auction.floorPrice}
        </p>
      </div>
    );
  }

  if (
    (productState === 'AVAILABLE' || productState === 'SOLD_OUT') &&
    isReleaseTypeBlockchainAuction(releaseType || '') &&
    auction?.manifoldListingId
  ) {
    return (
      <span className="product-hero">
        <AuctionPrice
          className="justify-center text-md md:gap-2 md:text-lg"
          defaultValue={auction.floorPrice}
          isRanked={releaseType === 'nft_ranked_auction'}
          listingId={auction.manifoldListingId}
          prefix={productState === 'AVAILABLE' ? t('auction.from') : undefined}
        />
      </span>
    );
  }

  return null;
};
